import { useCallback, useMemo } from 'react';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import ButtonBase from '@zenbusiness/zui/ButtonBase';

import { getTimelineItems } from '#client/components/OrderTracker/utils';
import { ENABLE_ORDER_TRACKER_RA_IR } from '#client/constants/experimentsFlags';
import { useUrlResolver } from '#client/hooks/useUrlResolver';

import { useEnterpriseFulfillments } from './useEnterpriseFulfillments';

// Fulfillment statuses where the customer can act to move the service forward.
const ACTIONABLE_STATUSES = ['action_req', 'incomplete'];

/**
 * Logic for the enterprise OrderTracker: reads orders-api fulfillments, builds the
 * timeline items (live status), and attaches the per-row link props for actionable
 * (link) rows. Display labels are resolved by the component.
 *
 * @returns {{
 *   loading: boolean,
 *   items: Array<object>
 * }}
 */
export function useEnterpriseOrderTracker() {
  const { items: fulfillments, loading } = useEnterpriseFulfillments();
  const resolve = useUrlResolver();
  const enableRaIr = useExperimentFlag(ENABLE_ORDER_TRACKER_RA_IR, false);

  // The resolver's `journey` kind reproduces the jurisdiction-segment logic this hook
  // used to inline (insert `/:jurisdiction/` before the uuid when known). An unmapped
  // type or a missing businessEntityUuid both resolve to an empty url — non-navigable —
  // so projecting `.url` preserves the prior status-only behavior.
  const getServicePath = useCallback(
    (type, jurisdictionCode) => resolve(type, { context: 'enterprise', jurisdictionCode }).url,
    [resolve],
  );

  const getLinkProps = useCallback(
    (item) => {
      const href = ACTIONABLE_STATUSES.includes(item.status) ? getServicePath(item.type, item.jurisdictionCode) : '';

      // reserveChevronSpace keeps the status pill in a consistent right-side
      // column across actionable (chevron) and status-only rows (GROWTH-4667).
      if (href) {
        return {
          component: ButtonBase,
          clickstreamProps: { elementName: 'enterprise_order_tracker_item' },
          href,
          isClickable: true,
          reserveChevronSpace: true,
          showChevron: true,
        };
      }

      return {
        isClickable: false,
        reserveChevronSpace: true,
        showChevron: false,
      };
    },
    [getServicePath],
  );

  const timelineItems = useMemo(
    () => getTimelineItems(fulfillments, { keepLiveStatus: true, enableRaIr }),
    [fulfillments, enableRaIr],
  );

  const items = useMemo(
    () =>
      timelineItems.map((item) => ({
        ...item,
        linkProps: getLinkProps(item),
      })),
    [timelineItems, getLinkProps],
  );

  return {
    items,
    loading,
  };
}
